// Tariflar (Q-064, Q-079): narx, tarkib (bo'limlar, limitlar, xodim va filial soni) va qo'shimchalar narxi.
// Platforma egasi va billing o'zgartiradi — kodsiz, jurnalga yoziladi. Narx oshsa, mavjud restoranlarga eski narx
// ogohlantirish muddati davomida saqlanadi; tarkib o'zgarsa, restoranlardagi bo'limlar o'chirilmaydi.
import { useState } from 'react';
import { MODULES, addonPriceLabel, getModule, parseAmount } from '@onepos/shared';
import { Alert, Badge, Button, Card, Icon, Modal, Spinner, Switch, TextAreaField, TextField, useAction, useAsync } from '@onepos/ui';
import { api } from '../lib/api';
import { useMe } from '../lib/auth';
import { LIMIT_LABELS, fmtMoney } from '../lib/format';
import type { BillingSettings, Plan } from '../lib/types';

const cap = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);
/** Tarifga qo'shimcha sifatida sotilishi mumkin bo'lgan bo'limlar (savdo har doim tarifda) */
const SELLABLE = MODULES.filter((m) => m.status === 'available' && m.key !== 'sales');

export function PlansPage() {
  const me = useMe();
  const canEdit = me.role === 'owner' || me.role === 'billing';
  const { data, error, loading, reload } = useAsync(() => api.rpc<Plan[]>('platform_plans'), []);
  const [editing, setEditing] = useState<{ plan: Plan; what: 'price' | 'content' } | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  return (
    <>
      <header className="cn-page-head">
        <div>
          <h1>Tariflar</h1>
          <p>Narx, tarkib va qo‘shimchalar. Narx oshsa — mavjud restoranlarga eski narx ogohlantirish muddati davomida saqlanadi.</p>
        </div>
      </header>
      {error && <Alert tone="danger">{error.message}</Alert>}
      {notice && <Alert tone="info">{notice}</Alert>}
      {loading && !data ? (
        <Spinner />
      ) : (
        <div className="cn-grid">
          {data?.map((p) => (
            <PlanCard key={p.id} plan={p} canEdit={canEdit} onEdit={(what) => setEditing({ plan: p, what })} />
          ))}
        </div>
      )}
      <BillingSettingsCard canEdit={canEdit} />
      {editing?.what === 'price' && (
        <PriceModal
          plan={editing.plan}
          onClose={() => setEditing(null)}
          onDone={(locked) => {
            reload();
            setNotice(locked > 0 ? `Narx saqlandi. ${locked} ta restoranga eski narx ogohlantirish muddati davomida saqlandi.` : 'Narx saqlandi.');
          }}
        />
      )}
      {editing?.what === 'content' && (
        <ContentModal
          plan={editing.plan}
          onClose={() => setEditing(null)}
          onDone={(outside) => {
            reload();
            setNotice(
              outside > 0
                ? `Tarkib saqlandi. ${outside} ta restoranda tarifdan tashqari bo‘lim yoqilgan — ular o‘chirilmadi; narxi qo‘yilgan bo‘lsa, qo‘shimcha sifatida hisoblanadi.`
                : 'Tarkib saqlandi.',
            );
          }}
        />
      )}
    </>
  );
}

function PlanCard({ plan: p, canEdit, onEdit }: { plan: Plan; canEdit: boolean; onEdit: (what: 'price' | 'content') => void }) {
  const ap = p.addon_prices ?? {};
  const modulePrices = Object.entries(ap).filter(([k]) => k.startsWith('module.'));
  return (
    <Card
      title={p.label}
      actions={<Badge tone={p.is_active ? 'success' : 'neutral'}>{p.is_active ? `${p.tenants ?? 0} restoran` : 'Yangilarga yopiq'}</Badge>}
    >
      <p className="cn-big op-num" style={{ margin: '0 0 12px' }}>
        {p.monthly_price === null ? 'Narx belgilanmagan' : `${fmtMoney(p.monthly_price)} / oy`}
      </p>
      <dl className="op-dl">
        <div className="op-dl__row">
          <dt>Filiallar</dt>
          <dd className="op-num">
            {p.max_branches}
            {ap.branch ? <span className="cn-muted"> · 2-dan +{fmtMoney(ap.branch)}</span> : null}
          </dd>
        </div>
        <div className="op-dl__row">
          <dt>Xodimlar</dt>
          <dd className="op-num">{p.max_staff}</dd>
        </div>
        <div className="op-dl__row">
          <dt>Ega qurilmalari</dt>
          <dd>cheksiz</dd>
        </div>
        {Object.entries(LIMIT_LABELS).map(([k, label]) => (
          <div key={k} className="op-dl__row">
            <dt>{cap(label)}</dt>
            <dd className="op-num">
              {p.limits?.[k] ?? 0}
              {ap[k] ? (
                <span className="cn-muted">
                  {' '}
                  · +{fmtMoney(ap[k]!)}
                  {(ap[`pack.${k}`] ?? 1) > 1 ? ` / ${ap[`pack.${k}`]} talik paket` : ''}
                </span>
              ) : null}
            </dd>
          </div>
        ))}
        {modulePrices.map(([k, v]) => (
          <div key={k} className="op-dl__row">
            <dt>{addonPriceLabel(k)}</dt>
            <dd className="op-num">+{fmtMoney(v)}</dd>
          </div>
        ))}
      </dl>
      <div className="cn-tags" style={{ marginTop: 12 }}>
        {p.modules.map((m) => (
          <span key={m} className="cn-tag">
            {getModule(m)?.label ?? m}
          </span>
        ))}
      </div>
      {canEdit && (
        <div className="cn-row" style={{ marginTop: 14 }}>
          <Button size="sm" onClick={() => onEdit('price')}>
            <Icon name="edit" /> Narx
          </Button>
          <Button size="sm" variant="ghost" onClick={() => onEdit('content')}>
            <Icon name="sliders" /> Tarkib
          </Button>
        </div>
      )}
    </Card>
  );
}

const digits = (t: string) => t.replace(/[^\d\s]/g, '');
const num = (t: string) => (t.trim() === '' ? null : parseAmount(t));
const badAmount = (t: string) => t.trim() !== '' && (num(t) === null || !Number.isInteger(num(t)!));

function PriceModal({ plan, onClose, onDone }: { plan: Plan; onClose: () => void; onDone: (locked: number) => void }) {
  const ap = plan.addon_prices ?? {};
  const [monthly, setMonthly] = useState(plan.monthly_price === null ? '' : String(plan.monthly_price));
  const keys = [
    ...Object.keys(LIMIT_LABELS),
    ...(plan.max_branches > 1 ? ['branch'] : []),
    ...SELLABLE.filter((m) => !plan.modules.includes(m.key)).map((m) => `module.${m.key}`),
  ];
  const [prices, setPrices] = useState<Record<string, string>>(() => Object.fromEntries(keys.map((k) => [k, ap[k] ? String(ap[k]) : ''])));
  const [packs, setPacks] = useState<Record<string, string>>(() =>
    Object.fromEntries(Object.keys(LIMIT_LABELS).map((k) => [k, ap[`pack.${k}`] ? String(ap[`pack.${k}`]) : ''])),
  );
  const [days, setDays] = useState('30');
  const badPack = (t: string) => t.trim() !== '' && !(/^\d{1,2}$/.test(t.trim()) && Number(t) >= 1 && Number(t) <= 50);
  const badDays = !/^\d{1,3}$/.test(days) || Number(days) > 365;
  const invalid = badAmount(monthly) || Object.values(prices).some(badAmount) || Object.values(packs).some(badPack) || badDays;
  const act = useAction(async () => {
    const payload: Record<string, number> = {};
    for (const [k, v] of Object.entries(prices)) {
      const n = num(v);
      if (n !== null && n > 0) payload[k] = n;
    }
    for (const [k, v] of Object.entries(packs)) {
      if (v.trim() !== '' && Number(v) > 1) payload[`pack.${k}`] = Number(v);
    }
    const r = await api.rpc<Plan & { locked: number }>('platform_plan_prices_set', {
      p_plan: plan.id,
      p_monthly: num(monthly),
      p_addons: payload,
      p_notice_days: Number(days),
    });
    onDone(r?.locked ?? 0);
    onClose();
  });
  return (
    <Modal
      open
      onClose={onClose}
      busy={act.busy}
      title={`${plan.label} tarifi narxi`}
      footer={
        <>
          <Button onClick={onClose} disabled={act.busy}>
            Bekor qilish
          </Button>
          <Button variant="primary" disabled={act.busy || invalid} onClick={() => void act.run()}>
            Saqlash
          </Button>
        </>
      }
    >
      {act.error && <Alert tone="danger">{act.error}</Alert>}
      <TextField
        label="Oylik narx, so‘m"
        inputMode="numeric"
        value={monthly}
        onChange={(e) => setMonthly(digits(e.target.value))}
        hint="Bo‘sh qoldirilsa — narx belgilanmagan (summa hisoblanmaydi)"
        error={badAmount(monthly) ? 'Summa noto‘g‘ri' : undefined}
      />
      <div className="cn-field-label">Qurilmalar: tarifdagidan ortiq — oyiga (paket bo‘lsa — har boshlangan paket uchun)</div>
      <div className="cn-form-grid">
        {Object.entries(LIMIT_LABELS).map(([k, label]) => (
          <div key={k} className="cn-row" style={{ alignItems: 'flex-start' }}>
            <TextField
              label={`${cap(label)} (tarifda ${plan.limits?.[k] ?? 0})`}
              inputMode="numeric"
              value={prices[k] ?? ''}
              placeholder="0"
              onChange={(e) => setPrices({ ...prices, [k]: digits(e.target.value) })}
              error={badAmount(prices[k] ?? '') ? 'Summa noto‘g‘ri' : undefined}
            />
            <TextField
              label="Paket, dona"
              inputMode="numeric"
              value={packs[k] ?? ''}
              placeholder="1"
              onChange={(e) => setPacks({ ...packs, [k]: e.target.value.replace(/\D/g, '').slice(0, 2) })}
              error={badPack(packs[k] ?? '') ? '1–50' : undefined}
            />
          </div>
        ))}
      </div>
      <div className="cn-field-label">Filial va tarifdan tashqari bo‘limlar — oyiga (bo‘sh — sotilmaydi yoki bepul)</div>
      <div className="cn-form-grid">
        {keys
          .filter((k) => k === 'branch' || k.startsWith('module.'))
          .map((k) => (
            <TextField
              key={k}
              label={k === 'branch' ? 'Qo‘shimcha filial (2-dan boshlab)' : addonPriceLabel(k)}
              inputMode="numeric"
              value={prices[k] ?? ''}
              placeholder="—"
              onChange={(e) => setPrices({ ...prices, [k]: digits(e.target.value) })}
              error={badAmount(prices[k] ?? '') ? 'Summa noto‘g‘ri' : undefined}
            />
          ))}
      </div>
      <TextField
        label="Narx oshsa, mavjud restoranlarga eski narx necha kun saqlanadi"
        inputMode="numeric"
        value={days}
        onChange={(e) => setDays(e.target.value.replace(/\D/g, '').slice(0, 3))}
        hint="0 — darhol. Narx tushsa — hammaga darhol arzoni qo‘llanadi."
        error={badDays ? '0–365 kun' : undefined}
      />
    </Modal>
  );
}

function ContentModal({ plan, onClose, onDone }: { plan: Plan; onClose: () => void; onDone: (outside: number) => void }) {
  const [label, setLabel] = useState(plan.label);
  const [mods, setMods] = useState<string[]>(() => [...plan.modules]);
  const [limits, setLimits] = useState<Record<string, string>>(() =>
    Object.fromEntries(Object.keys(LIMIT_LABELS).map((k) => [k, String(plan.limits?.[k] ?? 0)])),
  );
  const [staff, setStaff] = useState(String(plan.max_staff));
  const [branches, setBranches] = useState(String(plan.max_branches));
  const [active, setActive] = useState(plan.is_active);
  const intOk = (t: string, min: number, max: number) => /^\d{1,5}$/.test(t) && Number(t) >= min && Number(t) <= max;
  const missing = mods.flatMap((k) => (getModule(k)?.requires ?? []).filter((r) => !mods.includes(r)).map((r) => [k, r] as const));
  const invalid =
    label.trim().length === 0 ||
    label.trim().length > 40 ||
    Object.values(limits).some((v) => !intOk(v, 0, 999)) ||
    !intOk(staff, 1, 10000) ||
    !intOk(branches, 1, 100) ||
    missing.length > 0;
  const toggle = (k: string, on: boolean) => setMods(on ? [...mods, k] : mods.filter((x) => x !== k));
  const act = useAction(async () => {
    const r = await api.rpc<Plan & { outside_tenants: number }>('platform_plan_update', {
      p_plan: plan.id,
      p_data: {
        label: label.trim(),
        modules: ['sales', ...mods.filter((m) => m !== 'sales')],
        limits: Object.fromEntries(Object.entries(limits).map(([k, v]) => [k, Number(v)])),
        max_staff: Number(staff),
        max_branches: Number(branches),
        is_active: active,
      },
    });
    onDone(r?.outside_tenants ?? 0);
    onClose();
  });
  return (
    <Modal
      open
      onClose={onClose}
      busy={act.busy}
      title={`${plan.label} tarifi tarkibi`}
      footer={
        <>
          <Button onClick={onClose} disabled={act.busy}>
            Bekor qilish
          </Button>
          <Button variant="primary" disabled={act.busy || invalid} onClick={() => void act.run()}>
            Saqlash
          </Button>
        </>
      }
    >
      {act.error && <Alert tone="danger">{act.error}</Alert>}
      <p className="cn-muted" style={{ marginTop: 0 }}>
        O‘zgarish shu tarifdagi hamma restoranga ta’sir qiladi. Restoranlardagi bo‘limlar o‘chirilmaydi, ishlab turgan qurilmalar uzilmaydi —
        limit kamaysa, faqat yangilari qo‘shilmaydi.
      </p>
      <TextField label="Nomi" value={label} maxLength={40} onChange={(e) => setLabel(e.target.value)} />
      <fieldset className="cn-checks">
        <legend className="op-field__label">Bo‘limlar</legend>
        <label className="cn-check">
          <input type="checkbox" checked disabled /> {getModule('sales')?.label} (har doim)
        </label>
        {SELLABLE.map((m) => (
          <label key={m.key} className="cn-check">
            <input type="checkbox" checked={mods.includes(m.key)} onChange={(e) => toggle(m.key, e.target.checked)} /> {m.label}
          </label>
        ))}
        {missing.map(([k, r]) => (
          <span key={`${k}-${r}`} className="op-field__error">
            “{getModule(k)?.label}” uchun “{getModule(r)?.label}” ham kerak
          </span>
        ))}
      </fieldset>
      <div className="cn-field-label">Tarif ichidagi qurilmalar</div>
      <div className="cn-form-grid">
        {Object.entries(LIMIT_LABELS).map(([k, l]) => (
          <TextField
            key={k}
            label={cap(l)}
            inputMode="numeric"
            value={limits[k] ?? '0'}
            onChange={(e) => setLimits({ ...limits, [k]: e.target.value.replace(/\D/g, '').slice(0, 3) })}
            error={intOk(limits[k] ?? '', 0, 999) ? undefined : '0–999'}
          />
        ))}
        <TextField
          label="Xodimlar"
          inputMode="numeric"
          value={staff}
          onChange={(e) => setStaff(e.target.value.replace(/\D/g, '').slice(0, 5))}
          error={intOk(staff, 1, 10000) ? undefined : '1–10 000'}
        />
        <TextField
          label="Filiallar"
          inputMode="numeric"
          value={branches}
          onChange={(e) => setBranches(e.target.value.replace(/\D/g, '').slice(0, 3))}
          error={intOk(branches, 1, 100) ? undefined : '1–100'}
        />
      </div>
      <Switch
        checked={active}
        onChange={setActive}
        label="Yangi restoranlarga taklif qilinadi"
        description="O‘chirilsa — yangi restoran ochishda ko‘rinmaydi; mavjud restoranlar shu tarifda qoladi"
      />
    </Modal>
  );
}

function BillingSettingsCard({ canEdit }: { canEdit: boolean }) {
  const s = useAsync(() => api.rpc<BillingSettings>('platform_billing_settings'), []);
  const [edit, setEdit] = useState(false);
  return (
    <Card
      title="Chegirmalar va to‘lov rekvizitlari"
      actions={
        canEdit &&
        s.data && (
          <Button size="sm" variant="ghost" onClick={() => setEdit(true)}>
            <Icon name="edit" /> O‘zgartirish
          </Button>
        )
      }
    >
      {s.error && <Alert tone="danger">{s.error.message}</Alert>}
      {s.loading && !s.data ? (
        <Spinner />
      ) : s.data ? (
        <>
          <div className="cn-tags">
            {['3', '6', '12'].map((k) => (
              <span key={k} className="cn-tag">
                {k === '12' ? '1 yil' : `${k} oy`}: {s.data!.discounts[k] ? `−${s.data!.discounts[k]}%` : 'chegirmasiz'}
              </span>
            ))}
          </div>
          <p className="cn-muted" style={{ whiteSpace: 'pre-line', marginBottom: 0 }}>
            {s.data.requisites || 'Rekvizitlar kiritilmagan — restoran egasi panelida “qanday to‘lash” ko‘rinmaydi.'}
          </p>
        </>
      ) : null}
      {edit && s.data && <SettingsModal initial={s.data} onClose={() => setEdit(false)} onDone={s.reload} />}
    </Card>
  );
}

function SettingsModal({ initial, onClose, onDone }: { initial: BillingSettings; onClose: () => void; onDone: () => void }) {
  const [disc, setDisc] = useState<Record<string, string>>(() =>
    Object.fromEntries(['3', '6', '12'].map((k) => [k, String(initial.discounts[k] ?? 0)])),
  );
  const [req, setReq] = useState(initial.requisites);
  const ok = (t: string) => /^\d{1,2}$/.test(t) && Number(t) <= 50;
  const invalid = Object.values(disc).some((v) => !ok(v)) || req.length > 1000;
  const act = useAction(async () => {
    await api.rpc('platform_billing_settings_set', {
      p_discounts: Object.fromEntries(Object.entries(disc).map(([k, v]) => [k, Number(v)])),
      p_requisites: req,
    });
    onDone();
    onClose();
  });
  return (
    <Modal
      open
      onClose={onClose}
      busy={act.busy}
      title="Chegirmalar va rekvizitlar"
      footer={
        <>
          <Button onClick={onClose} disabled={act.busy}>
            Bekor qilish
          </Button>
          <Button variant="primary" disabled={act.busy || invalid} onClick={() => void act.run()}>
            Saqlash
          </Button>
        </>
      }
    >
      {act.error && <Alert tone="danger">{act.error}</Alert>}
      <div className="cn-form-grid">
        {['3', '6', '12'].map((k) => (
          <TextField
            key={k}
            label={`${k === '12' ? '1 yil' : `${k} oy`} uchun chegirma, %`}
            inputMode="numeric"
            value={disc[k] ?? '0'}
            onChange={(e) => setDisc({ ...disc, [k]: e.target.value.replace(/\D/g, '').slice(0, 2) })}
            error={ok(disc[k] ?? '') ? undefined : '0–50'}
          />
        ))}
      </div>
      <TextAreaField
        label="To‘lov rekvizitlari (restoran egasi ko‘radi)"
        rows={5}
        value={req}
        maxLength={1000}
        onChange={(e) => setReq(e.target.value)}
        placeholder={'Oluvchi: …\nBank: …\nH/r: …\nMFO: …  STIR: …'}
        hint="To‘lov izohiga restoran logini yoziladi — bu panelda o‘zi ko‘rsatiladi"
      />
    </Modal>
  );
}
